import {
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Separator } from '@/components/ui/separator';
import { useDayRollover } from '@/hooks/use-day-rollover';
import { usePermissions } from '@/modules/common';
import { PageTemplate } from '@/modules/common/components/templates/page-template';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { formatCompactDateRange, formatDisplayDate } from '@/utils/date-formatters';

import { DateNavigation } from '../components/date-navigation';
import { DayView } from '../components/day-view';
import { MonthView } from '../components/month-view';
import { UnifiedSummary, UnifiedSummarySkeleton } from '../components/summary';
import { TimeTracker } from '../components/time-tracker';
import { ViewTabs } from '../components/view-tabs';
import { WeekView } from '../components/week-view';
import { useSummary } from '../hooks/use-summary';
import { useViewModeAndDate } from '../hooks/use-view-mode-and-date';
import {
  TimeOffRequestSummaryStatus,
  TimeTrackerView,
  ViewMode,
  WeeklySummary,
} from '../types/time-tracking';

const getSummaryLabels = (
  viewMode: ViewMode,
  date: Date,
  t: (key: string) => string
): { label: string; sublabel: string } => {
  const now = new Date();
  if (viewMode === ViewMode.Day) {
    const label = isSameDay(date, now) ? t('summary.labels.today') : format(date, 'EEEE');
    return { label, sublabel: formatDisplayDate(date) };
  }
  if (viewMode === ViewMode.Week) {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
    const isThisWeek = now >= weekStart && now <= weekEnd;
    return {
      label: isThisWeek ? t('summary.labels.thisWeek') : t('summary.labels.week'),
      sublabel: formatCompactDateRange(weekStart, weekEnd),
    };
  }
  const isThisMonth = isSameMonth(date, now);
  return {
    label: isThisMonth ? t('summary.labels.thisMonth') : t('summary.labels.month'),
    sublabel: formatCompactDateRange(startOfMonth(date), endOfMonth(date)),
  };
};

const findTimeOffHours = (
  summary: WeeklySummary | undefined,
  status: TimeOffRequestSummaryStatus
): number => summary?.timeOffSummary.find(item => item.status === status)?.totalHours ?? 0;

const findTrackedBackgroundHours = (summary: WeeklySummary | undefined): number =>
  summary?.trackedBreakdown
    .filter(item => item.isBackgroundWork)
    .reduce((sum, item) => sum + item.trackedHours, 0) ?? 0;

export function TimeTrackingPage() {
  const { t } = useTranslation('authenticated.timeTracking');
  const { getErrorMessage } = useApiError();

  const { selectedDate, viewMode, goToToday } = useViewModeAndDate();

  // If the tab stays open past midnight and the user was viewing "today",
  // advance the selected date so they see the new day instead of yesterday.
  useDayRollover((now, previous) => {
    if (isSameDay(selectedDate, previous) && !isSameDay(selectedDate, now)) {
      goToToday();
    }
  });

  // Summary drives the unified summary card at the top. The Day view fetches its
  // own copy of this query — TanStack dedupes the request, so we pay only once.
  const {
    data: summary,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useSummary({
    date: selectedDate,
    viewMode,
  });

  const { hasPermission } = usePermissions();
  const canTrackTime = hasPermission(OpsiumPolicy.WorklogsCreate);

  const { label, sublabel } = getSummaryLabels(viewMode, selectedDate, t);

  const renderSummary = () => {
    if (isLoading) return <UnifiedSummarySkeleton />;
    if (error) {
      return (
        <div className="flex flex-col items-center gap-2 rounded-lg border bg-card p-6 shadow-sm">
          <ButtonLoadable
            variant="outline"
            size="sm"
            showRefreshIcon
            isLoading={isRefetching}
            onClick={() => refetch()}
          >
            {t('summary.retry')}
          </ButtonLoadable>
          <p className="text-xs text-destructive">{getErrorMessage(error)}</p>
        </div>
      );
    }
    if (!summary) return null;
    return (
      <UnifiedSummary
        label={label}
        sublabel={sublabel}
        tracked={summary.totalTrackedHours}
        trackedBackground={findTrackedBackgroundHours(summary)}
        booked={summary.totalBookedHours}
        effectiveCapacity={summary.effectiveCapacityHours}
        timeOffApproved={findTimeOffHours(summary, TimeOffRequestSummaryStatus.APPROVED)}
        timeOffPending={findTimeOffHours(summary, TimeOffRequestSummaryStatus.PENDING)}
      />
    );
  };

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      rightActions={
        <div className="flex items-center gap-4">
          {/* xl+: timer inline with title */}
          {canTrackTime && (
            <div className="hidden xl:block">
              <TimeTracker view={TimeTrackerView.Flat} />
            </div>
          )}
          {canTrackTime && <Separator orientation="vertical" className="hidden h-10 xl:block" />}
          {/* xl+: date nav inline with title */}
          <div className="hidden xl:block">
            <DateNavigation />
          </div>
          <Separator orientation="vertical" className="hidden h-10 xl:block" />
          {/* xl+: view tabs inline with title */}
          <div className="hidden xl:block">
            <ViewTabs />
          </div>
          {/* below xl: date nav inline with title */}
          <div className="block xl:hidden">
            <DateNavigation />
          </div>
        </div>
      }
      secondaryActions={
        <div className="flex flex-col gap-2 pb-4 xl:hidden">
          {/* below xl: timer left, ViewTabs right — same row */}
          <div className="flex items-center justify-between">
            {canTrackTime && <TimeTracker view={TimeTrackerView.Flat} />}
            <ViewTabs />
          </div>
        </div>
      }
      contentClassName="overflow-visible"
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-6 shadow-[0_1px_4px_0_hsl(var(--border))]"
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      {/* Fixed header section */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {/* Unified period summary (replaces Summary + CapacityProgressBar + TimeOffCard) */}
        {renderSummary()}

        {/* Day view: assigned-projects list with per-project progress + timer */}
        {viewMode === ViewMode.Day && <DayView />}

        {/* Week view: grid with time-off strip and per-day cells */}
        {viewMode === ViewMode.Week && <WeekView />}

        {/* Month view: full calendar + day-detail panel + projects-this-month */}
        {viewMode === ViewMode.Month && <MonthView />}
      </div>
    </PageTemplate>
  );
}
